import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from '@apollo/client'
import { Defer20220824Handler } from '@apollo/client/incremental'
import { LocalState } from '@apollo/client/local-state'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { ErrorLink } from '@apollo/client/link/error'
import apolloLogger from 'apollo-link-logger'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { getMainDefinition } from '@apollo/client/utilities'
import { setContext } from '@apollo/client/link/context'
import { createClient as createWsClient } from 'graphql-ws'
import config from './clientConfig'
import { setCookieKey, tokenGet } from './pintor'

const createAuthLink = token =>
  setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: token || ''
    }
  }))

const getSSRLinks = ({ token, link }) => {
  const authLink = createAuthLink(token)

  return ApolloLink.concat(authLink, link);
}

export const createClient = ({
  ssr,
  ssrLink,
  req,
  token,
  uri,
  cache,
  WS,
  wsUri,
  addClientContext,
  defaultOptions,
  skipLogging
} = {}) => {
  let useLink

  let httpOptions = {
    uri: uri,
    credentials: "same-origin",
    fetch,
  }
  if (ssr && req) {
    httpOptions.headers = {
      cookie: req.header('Cookie')
    }
  }

  if (ssr) {
    let link

    if (ssrLink)
      link = ssrLink
    else
      link = new HttpLink(httpOptions)

    useLink = getSSRLinks({ token, link })
    if (addClientContext) {
      const extraContext = setContext((_, { headers }) => ({
        headers: {
          ...headers,
          ...addClientContext(headers)
        }
      }))

      useLink = ApolloLink.concat(useLink, extraContext)
    }
  } else {
    let connectionParams = { authToken: token }
    if (addClientContext) {
      connectionParams = {
        ...connectionParams,
        ...addClientContext()
      }
    }

    const wsClient = new createWsClient({
      url: wsUri,
      connectionParams,
      on: {
        connected: () => console.log("Connected to ws"),
        closed: (event) => console.log("websockets closed", event),
        error: (err) => console.error("websockets error", err)
      }
    }, WS)

    const wsLink = new GraphQLWsLink(wsClient)
    const uploadLink = ApolloLink.concat(
      createAuthLink(token),
      new UploadHttpLink({
        ...httpOptions,
        headers: {'Apollo-Require-Preflight': true}
      })
    )

    useLink = ApolloLink.split(({ query }) => {
      const { name: { value } } = getMainDefinition(query)

      return value == 'Upload'
    }, uploadLink, wsLink)
  }

  let link = skipLogging ? [] : [ apolloLogger ]
  link.push(
    new ErrorLink(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.map((entry) => {
          const message = JSON.stringify(entry, null, '\t')

          console.error(
            `[GraphQL client error] ${message}`
          )
        })
      }
      if (networkError) {
        const structured = JSON.stringify(networkError)
        console.error(`[Network error in graphql client]: ${structured}`)
      }
    })
  )
  link.push(useLink)

  return new ApolloClient({
    ssrMode: ssr,
    link: ApolloLink.from(link),
    cache: cache || new InMemoryCache(),
    defaultOptions,
    // link: new HttpLink({}),

    /*
    Inserted by Apollo Client 3->4 migration codemod.
    If you are not using the `@client` directive in your application,
    you can safely remove this option.
    */
    localState: new LocalState({}),

    /*
    Inserted by Apollo Client 3->4 migration codemod.
    If you are not using the `@defer` directive in your application,
    you can safely remove this option.
    */
    incrementalHandler: new Defer20220824Handler(),
  });
}

export function createApolloClient({
  ssr,
  ssrLink,
  uri,
  wsUri,
  addClientContext
}) {
  let cache = new InMemoryCache()
  if (SSR != 'true')
    cache = cache.restore(window.__APOLLO_STATE__)

  setCookieKey(config.cookieKey)
  const token = tokenGet()

  const client = createClient({
    ssr,
    ssrLink,
    uri,
    wsUri,
    cache,
    token,
    addClientContext
  })

  return { client }
}
